import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, FileText, Calendar, DollarSign, TrendingUp, 
  AlertCircle, CheckCircle, Clock, Briefcase 
} from 'lucide-react';
import {
  independentClientsService,
  independentQuotesService,
  independentItinerariesService,
  independentAnalyticsService,
  independentLeadsService,
  independentTasksService,
  independentPaymentsService
} from '../../services/independent';

const IndependentDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [recentClients, setRecentClients] = useState([]);
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentTasks, setRecentTasks] = useState([]);
  const [upcomingItineraries, setUpcomingItineraries] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [
        analyticsData,
        clients,
        quotes,
        tasks,
        itineraries
      ] = await Promise.all([
        independentAnalyticsService.getDashboard({ period: '30' }),
        independentClientsService.getAll(),
        independentQuotesService.getAll(),
        independentTasksService.getAll(),
        independentItinerariesService.getAll()
      ]);

      setAnalytics(analyticsData);
      setRecentClients(clients.slice(0, 5));
      setRecentQuotes(quotes.slice(0, 5));
      setRecentTasks(tasks.filter(t => t.status !== 'completed').slice(0, 5));
      
      const upcoming = itineraries
        .filter(i => new Date(i.start_date) > new Date())
        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
        .slice(0, 5);
      setUpcomingItineraries(upcoming);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Clients',
      value: analytics?.clients || 0,
      icon: Users,
      color: 'bg-blue-500',
      link: '/independent/clients'
    },
    {
      title: 'Active Quotes',
      value: analytics?.quotes?.total_quotes || 0,
      icon: FileText,
      color: 'bg-green-500',
      link: '/independent/quotes'
    },
    {
      title: 'Itineraries',
      value: analytics?.itineraries?.total_itineraries || 0,
      icon: Calendar,
      color: 'bg-purple-500',
      link: '/independent/itineraries'
    },
    {
      title: 'Revenue (30d)',
      value: `$${(analytics?.itineraries?.total_revenue || 0).toLocaleString()}`,
      icon: DollarSign,
      color: 'bg-yellow-500',
      link: '/independent/analytics'
    },
    {
      title: 'Active Leads',
      value: analytics?.leads?.new_leads || 0,
      icon: Briefcase,
      color: 'bg-indigo-500',
      link: '/independent/leads'
    },
    {
      title: 'Pending Tasks',
      value: recentTasks.length,
      icon: CheckCircle,
      color: 'bg-red-500',
      link: '/independent/tasks'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome back! Here's your business overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <Link
            key={index}
            to={stat.link}
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="text-white" size={24} />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Clients */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Clients</h2>
              <Link to="/independent/clients" className="text-blue-600 hover:text-blue-700 text-sm">
                View All
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentClients.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No clients yet</p>
            ) : (
              <div className="space-y-3">
                {recentClients.map((client) => (
                  <Link
                    key={client.id}
                    to={`/independent/clients/${client.id}`}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {client.first_name} {client.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{client.email}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      client.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {client.status}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Quotes */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Quotes</h2>
              <Link to="/independent/quotes" className="text-blue-600 hover:text-blue-700 text-sm">
                View All
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentQuotes.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No quotes yet</p>
            ) : (
              <div className="space-y-3">
                {recentQuotes.map((quote) => (
                  <Link
                    key={quote.id}
                    to={`/independent/quotes/${quote.id}`}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{quote.trip_name}</p>
                      <p className="text-sm text-gray-500">{quote.destination}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">${quote.total_price?.toLocaleString()}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        quote.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                        quote.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                        quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {quote.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pending Tasks */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Pending Tasks</h2>
              <Link to="/independent/tasks" className="text-blue-600 hover:text-blue-700 text-sm">
                View All
              </Link>
            </div>
          </div>
          <div className="p-6">
            {recentTasks.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No pending tasks</p>
            ) : (
              <div className="space-y-3">
                {recentTasks.map((task) => (
                  <div
                    key={task.id}
                    className="flex items-start p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{task.title}</p>
                      <p className="text-sm text-gray-500 mt-1">{task.description}</p>
                      {task.due_date && (
                        <p className="text-xs text-gray-400 mt-1">
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      task.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                      task.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {task.priority}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Itineraries */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Upcoming Itineraries</h2>
              <Link to="/independent/itineraries" className="text-blue-600 hover:text-blue-700 text-sm">
                View All
              </Link>
            </div>
          </div>
          <div className="p-6">
            {upcomingItineraries.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No upcoming itineraries</p>
            ) : (
              <div className="space-y-3">
                {upcomingItineraries.map((itinerary) => (
                  <Link
                    key={itinerary.id}
                    to={`/independent/itineraries/${itinerary.id}`}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{itinerary.trip_name}</p>
                      <p className="text-sm text-gray-500">{itinerary.destination}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(itinerary.start_date).toLocaleDateString()} - {new Date(itinerary.end_date).toLocaleDateString()}
                      </p>
                    </div>
                    <Clock className="text-gray-400" size={20} />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndependentDashboard;
